import { OrderDetailPage, Remark } from "@careinfo/layout";
import { tw } from "@careinfo/tailwind";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import APIS from "../../constant/EndPoint";
import DataTable from "../Reusable/DataTable";
import NACell from "../Reusable/DataTable/NACell";
import Modal from "../Reusable/Modal";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import OrderFilter from "../Consignment/ConsignmentFilter";
import { AccessControl } from "@careinfo/permission-required";
import Card from "../Reusable/Card";
import { hasValues } from "../../utils/hasValues";
import { useForm } from "react-hook-form";
import { IFilter } from "../Consignment/ConsignmentList";
import { useSessionStorage } from "@uidotdev/usehooks";
import Button from "../Reusable/Button";
import TabPanel from "../Reusable/Tab";

const HoldOrderList = ({ isLoading, handleRemove }: any) => {
  const [localState, handleSetState] = useSessionStorage<IFilter>(
    "consignment-filter",
    {
      is_rtv: null,
      order_remarks__isnull: null,
      is_walking_customer: null,
      delivery_status__in: null,
      pickup_branch: null,
      destination: null,
      payment_method: null,
      pickup_branch__warehouse: null,
      destination_branch__warehouse: null,
      vendor: null,
      created__range: null,
      sender: null,
      receiver: null,
      assign_order: null,
      staff: null,
      operations: null,
      comment: null,
    }
  );

  const {
    register,
    setValue,
    getValues,
    watch,
    setError,
    reset,
    clearErrors,
    control,
    formState: { errors },
  } = useForm<IFilter>({
    defaultValues: async () => ({
      is_rtv: localState?.is_rtv ?? null,
      order_remarks__isnull: localState?.order_remarks__isnull ?? null,
      is_walking_customer: localState?.is_walking_customer ?? null,
      delivery_status__in: localState?.delivery_status__in ?? null,
      pickup_branch: localState?.pickup_branch ?? null,
      destination: localState?.destination ?? null,
      payment_method: localState?.payment_method ?? null,
      pickup_branch__warehouse: localState?.pickup_branch__warehouse ?? null,
      destination_branch__warehouse:
        localState?.destination_branch__warehouse ?? null,
      vendor: localState?.vendor ?? null,
      created__range: localState?.created__range ?? null,
      sender: localState?.sender ?? null,
      receiver: localState?.receiver ?? null,
      assign_order: localState?.assign_order ?? null,
      staff: localState?.staff ?? null,
      operations: localState?.operations ?? null,
      comment: null,
    }),
  });

  const [isFilterVisible, setFilterVisiable] = useState(hasValues(localState));
  const [searchParams, setSearchParms] = useSearchParams();
  const [openorderhistoryPopup, setOpenOrderHistoryPopup] = useState(false);
  const [showReamarksPopup, setShowRemarksPopup] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [receiver, setReceiver] = useState("");

  const handleRemarksClick = (orderId: string, receiverName: string) => {
    setSelectedOrder(orderId);
    setReceiver(receiverName);
    setShowRemarksPopup(true);
  };

  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const selectedOrderDetails = searchParams.get("selectedItem");
  const location = useLocation();
  const navigate = useNavigate();

  const appendSelectedItem = useCallback(
    (selectedItem: any) => {
      const params = new URLSearchParams(location.search); // Get current search params
      params.set("selectedItem", selectedItem); // Add or update the "selectedItems" parameter

      // Construct the new full path
      const newFullPath = `${location.pathname}?${params.toString()}`;

      navigate(newFullPath, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );

  const handleFilterReset = () => {
    reset({
      is_rtv: null,
      order_remarks__isnull: null,
      is_walking_customer: null,
      delivery_status__in: null,
      pickup_branch: null,
      destination: null,
      payment_method: null,
      pickup_branch__warehouse: null,
      destination_branch__warehouse: null,
      vendor: null,
      created__range: null,
      sender: null,
      receiver: null,
      assign_order: null,
      staff: null,
      operations: null,
    });
  };

  const handleIndividualSelect = useCallback(
    (value: string) => {
      let selectedItems = searchParams.get("selectedItems")?.split(",");
      if (!selectedItems || selectedItems === undefined) {
        searchParams.set("selectedItems", value);
        setSearchParms(searchParams);
        return;
      }
      let itemIndex = selectedItems?.findIndex((item) => item === value);
      if (itemIndex > -1) {
        selectedItems.splice(itemIndex, 1);
        const newSelectedItems = selectedItems
          ?.map((item) => item)
          .join(",")
          .toString();
        if (newSelectedItems.length) {
          searchParams.set("selectedItems", newSelectedItems);
        } else {
          searchParams.delete("selectedItems");
        }
        searchParams.set("selectAllItems", "0");
        setSearchParms(searchParams);
      } else {
        selectedItems?.push(value);
        const newSelectedItems = selectedItems
          ?.map((item) => item)
          .join(",")
          .toString();
        searchParams.set("selectedItems", newSelectedItems);
        setSearchParms(searchParams);
      }
    },
    [searchParams, setSearchParms]
  );

  const isSelected = useCallback(
    (value: string) => {
      const selectedItems = searchParams.get("selectedItems")?.split(",");
      if (!selectedItems) {
        return false;
      }
      return selectedItems.includes(value);
    },
    [searchParams]
  );

  const handleSelectAll = useCallback(
    (table: any) => {
      const {
        options: { data: tableData },
      } = table;
      const isSelected = searchParams.get("selectAllItems");
      if (isSelected === "1") {
        searchParams.set("selectAllItems", "0");
        searchParams.delete("selectedItems");
        setSearchParms(searchParams);
      } else {
        const selectedItems = tableData
          ?.map((item: any) => item.id)
          .join(",")
          .toString();
        searchParams.set("selectAllItems", "1");
        searchParams.set("selectedItems", selectedItems);
        setSearchParms(searchParams);
      }
    },
    [searchParams, setSearchParms]
  );

  useEffect(() => {
    if (selectedOrderDetails) {
      setOpenOrderHistoryPopup(true);
    } else {
      setOpenOrderHistoryPopup(false);
    }
  }, [selectedOrderDetails]);

  const handleClosePopup = () => {
    setOpenOrderHistoryPopup(false);
    setTimeout(() => {
      const params = new URLSearchParams(location.search);
      params.delete("selectedItem");
      const newFullPath = `${location.pathname}?${params.toString()}`;
      navigate(newFullPath, { replace: true });
    }, 300);
  };

  const filterParams = watch();
  const defaultColumns = useMemo(
    (): ColumnDef<any, any>[] => [
      {
        id: "select",
        header: ({ table }: any) => {
          return (
            <input
              type="checkbox"
              checked={checkedAllItems}
              onChange={() => handleSelectAll(table)}
              className={tw`cursor-pointer`}
            />
          );
        },
        cell: ({ row }: any) => (
          <input
            type="checkbox"
            checked={isSelected(row?.original?.id)}
            onChange={() => handleIndividualSelect(row?.original?.id)}
            className={tw`cursor-pointer`}
          />
        ),
      },
      {
        header: "WayBill",
        accessorKey: "tracking_number",
        cell: ({ row, getValue }: any) => {
          return (
            <button
              onClick={() => {
                appendSelectedItem(row?.original?.id);
              }}
              className={tw`cursor-pointer focus:outline-none px-4 relative`}
            >
              <p className={tw`whitespace-nowrap`}>{getValue() ?? "N/A"}</p>
              <p className={tw`text-xs text-gray-500`}></p>
            </button>
          );
        },
      },
      {
        header: "Date",
        accessorKey: "created",
        cell: ({ row, getValue }: any) => {
          return (
            <>
              {getValue() ? (
                <p>{moment(getValue()).format("YY-MM-DD HH:mm:ss")}</p>
              ) : (
                "N/A"
              )}
            </>
          );
        },
      },
      {
        header: "Origin",
        accessorKey: "pickup_branch.name",
        cell: ({ row, getValue }: any) => {
          return (
            <>
              <p className={tw`uppercase`}>{getValue()}</p>
              <p className={tw`text-xs text-primary`}>
                {row?.original?.pickup_branch?.warehouse?.name}
              </p>
            </>
          );
        },
      },
      {
        header: "Sender",
        accessorKey: "sender.full_name",
        cell: NACell,
      },
      {
        header: "Receiver",
        accessorKey: "receiver.full_name",
        cell: NACell,
      },
      {
        header: "Destination",
        accessorKey: "destination.name",
        cell: ({ row, getValue }: any) => {
          return (
            <>
              <p className={tw`uppercase`}>{getValue()}</p>
              <p className={tw`text-xs text-primary`}>
                {row?.original?.destination_branch?.warehouse?.name}
              </p>
            </>
          );
        },
      },

      {
        header: "Piece",
        accessorKey: "piece",
        cell: ({ row, getValue }: any) => {
          return (
            <>
              <p className={tw`cursor-pointer`}>{getValue() ?? 0.0}</p>
            </>
          );
        },
      },
      {
        header: "Remarks",
        accessorKey: "remark",
        cell: ({ row, getValue }: any) => (
          <button
            onClick={() =>
              handleRemarksClick(
                row?.original?.id,
                row?.original?.receiver.full_name
              )
            }
          >
            {getValue()} Remarks
          </button>
        ),
      },
      {
        header: "Status",
        accessorKey: "delivery_status.name",
        cell: ({ row, getValue }: any) => {
          return (
            <>
              <p
                className={tw`uppercase px-1 text-center rounded-md`}
                style={{ background: row?.original?.delivery_status?.color }}
              >
                {getValue()}
              </p>
              <p className={tw`text-xs text-primary`}>
                {row?.original?.rider?.name}
              </p>
            </>
          );
        },
      },
    ],
    [isLoading, appendSelectedItem]
  );

  useEffect(() => {
    handleSetState({
      ...localState,
      ...filterParams,
      delivery_status__in: filterParams?.delivery_status__in
        ? filterParams.delivery_status__in.map((d) => ({
            id: d.value,
            name: d.label,
            label: d.label,
            value: d.value,
          }))
        : localState?.delivery_status__in,
    });
  }, [filterParams]);

  const filters = useCallback(() => {
    const newValue = {};
    for (const [key, value] of Object.entries(filterParams)) {
      if (value) {
        if (Array.isArray(value)) {
          newValue[key] = value.map((v) => v.value).join(",");
        } else if (typeof value === "object") {
          newValue[key] = value.id;
        } else if (!value) {
          newValue[key] = null;
        } else {
          newValue[key] = value;
        }
      }
    }
    return newValue;
  }, [filterParams]);
  return (
    <>
      <AccessControl
        children={
          <Card
            title="Filters"
            onClick={() => setFilterVisiable(!isFilterVisible)}
          >
            <OrderFilter
              isFilterVisible={isFilterVisible}
              register={register}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
              reset={handleFilterReset}
              watch={watch}
              clearErrors={clearErrors}
              //@ts-ignore
              control={control}
            />
          </Card>
        }
        allowedPermissions={["can_edit_filter"]}
      />
      <TabPanel
        tabTitle={["Auto Hold", "Manual Hold"]}
        tabPanels={[
          <div className={tw`flex flex-col mb-4`}>
            <DataTable
              url={APIS.ORDER}
              queryKey={"shipment-upload"}
              columns={defaultColumns}
              tableName={"shipment-upload-table"}
              params={{
                delivery_status__in: "918b8bf2-cc0a-4c43-be9c-c864f3416808",
                ...Object.fromEntries(
                  Object.entries(filters()).filter(
                    ([key]) => key !== "delivery_status__in"
                  )
                ),
              }}
              actionMenu={
                <>
                  <div className={tw`flex w-full justify-end`}>
                    <div className={tw`w-24`}>
                      <Button
                        type="button"
                        label="Remove"
                        isLoading={isLoading}
                        onClick={handleRemove}
                      />
                    </div>
                  </div>
                </>
              }
            />
          </div>,
          <div className={tw`flex flex-col mb-4`}>
            <DataTable
              url={APIS.ORDER}
              queryKey={"hold-order"}
              columns={defaultColumns}
              tableName={"hold-order-table"}
              params={{
                is_on_hold: true,
                ...filters(),
              }}
              actionMenu={
                <>
                  <div className={tw`flex w-full justify-end`}>
                    <div className={tw`w-24`}>
                      <Button
                        type="button"
                        label="Remove"
                        isLoading={isLoading}
                        onClick={handleRemove}
                      />
                    </div>
                  </div>
                </>
              }
            />
          </div>,
        ]}
      />
      <Modal
        isOpen={openorderhistoryPopup}
        title={"Order Detail"}
        setIsOpen={handleClosePopup}
      >
        <OrderDetailPage selectedOrder={selectedOrderDetails} />
      </Modal>
      <Modal
        isOpen={showReamarksPopup}
        setIsOpen={setShowRemarksPopup}
        title={"Remark"}
      >
        <div className={tw`w-[600px]`}>
          <Remark
            selectedOrder={selectedOrder}
            receiver={receiver}
            canInput={false}
          />
        </div>
      </Modal>
    </>
  );
};

export default HoldOrderList;
